import { compose } from 'redux';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var isObject = function isObject(value) {
  var type = typeof value;
  return type == "object" && value != null && !Array.isArray(value);
};

var createHandlers = function createHandlers(store, reducer, preloadedState, enhancer) {
  if (typeof enhancer !== 'undefined') {
    return enhancer(createHandlers)(store, reducer, preloadedState);
  }

  return {};
};

var splitOptions = function splitOptions(args) {
  var _args$slice = args.slice(-1),
      lastItem = _args$slice[0];

  return isObject(lastItem) ? [args.slice(0, -1), lastItem] : [args, {}];
};

var dynostore = function dynostore() {
  for (var _len = arguments.length, dynamicEnhancers = new Array(_len), _key = 0; _key < _len; _key++) {
    dynamicEnhancers[_key] = arguments[_key];
  }

  return function (createStore) {
    return function (reducer, preloadedState) {
      var _splitOptions = splitOptions(dynamicEnhancers),
          enhancers = _splitOptions[0],
          dynostoreOptions = _splitOptions[1];

      var store = _objectSpread2(_objectSpread2({}, createStore(reducer, preloadedState)), {}, {
        dynostoreOptions: dynostoreOptions
      });

      var dynamicHandlers = createHandlers(store, reducer, preloadedState, compose.apply(void 0, enhancers));
      return _objectSpread2(_objectSpread2({}, store), dynamicHandlers);
    };
  };
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var createMerge = function createMerge(overrideFunction) {
  return function (oldState, newState) {
    if (oldState === undefined || oldState === null || oldState === newState || typeof oldState !== typeof newState || !isObject(oldState) || !isObject(newState)) {
      return newState;
    }

    var hasChanged = false;
    var nextState = {};
    var usedKeys = new Set();
    Object.keys(newState).forEach(function (key) {
      var oldValue = oldState[key];
      var newValue = newState[key];
      var nextValue = overrideFunction(oldValue, newValue);
      nextState[key] = nextValue;
      usedKeys.add(key);
      hasChanged = hasChanged || nextValue !== oldValue;
    });

    if (hasChanged) {
      Object.keys(oldState).forEach(function (key) {
        if (!usedKeys.has(key)) {
          nextState[key] = oldState[key];
        }
      });
      return nextState;
    } else {
      return oldState;
    }
  };
};

var shallowMerge = /*#__PURE__*/createMerge(function (_, newValue) {
  return newValue;
});
var deepMerge = /*#__PURE__*/createMerge(function (oldValue, newValue) {
  return deepMerge(oldValue, newValue);
});

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var createStateHandler = function createStateHandler(merge) {
  return {
    createEmpty: function createEmpty() {
      return {};
    },
    getKeys: function getKeys(state) {
      return isObject(state) ? Object.keys(state) : [];
    },
    getValue: function getValue(state, key) {
      return state[key];
    },
    setValue: function setValue(state, key, value) {
      state[key] = value;
      return state;
    },
    canMerge: isObject,
    merge: merge
  };
};

var shallowStateHandler = /*#__PURE__*/createStateHandler(shallowMerge);
var deepStateHandler = /*#__PURE__*/createStateHandler(deepMerge);
var defaultStateHandler = shallowStateHandler;

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var combineReducers = function combineReducers(reducers, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$stateHandler = _ref.stateHandler;

  _ref$stateHandler = _ref$stateHandler === void 0 ? defaultStateHandler : _ref$stateHandler;
  var createEmpty = _ref$stateHandler.createEmpty,
      getValue = _ref$stateHandler.getValue,
      setValue = _ref$stateHandler.setValue;
  var reducerEntries = Object.entries(reducers);

  var combiner = function combiner(state, action) {
    if (state === void 0) {
      state = createEmpty();
    }

    var nextState = createEmpty();
    var hasChanged = false;
    reducerEntries.forEach(function (_ref2) {
      var key = _ref2[0],
          reducer = _ref2[1];
      var oldValue = getValue(state, key);
      var newValue = reducer(oldValue, action);
      nextState = setValue(nextState, key, newValue);
      hasChanged = hasChanged || newValue !== oldValue;
    });
    return hasChanged ? nextState : state;
  };

  return combiner;
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var mergeReducers = function mergeReducers(reducers, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$stateHandler = _ref.stateHandler;

  _ref$stateHandler = _ref$stateHandler === void 0 ? defaultStateHandler : _ref$stateHandler;
  var createEmpty = _ref$stateHandler.createEmpty,
      merge = _ref$stateHandler.merge;

  var merger = function merger(state, action) {
    var nextState = reducers.reduce(function (nextState, reducer) {
      var newState = reducer(state !== undefined ? nextState : undefined, action);
      return merge(nextState, newState);
    }, state);
    return nextState || createEmpty();
  };

  return merger;
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var FILTER_INIT = {
  type: '@@FILTER/INIT'
};

var filteredReducer = function filteredReducer(reducer, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$stateHandler = _ref.stateHandler;

  _ref$stateHandler = _ref$stateHandler === void 0 ? defaultStateHandler : _ref$stateHandler;
  var createEmpty = _ref$stateHandler.createEmpty,
      getKeys = _ref$stateHandler.getKeys,
      getValue = _ref$stateHandler.getValue,
      setValue = _ref$stateHandler.setValue;
  var initialState = reducer(undefined, FILTER_INIT);
  var knownKeys = getKeys(initialState);

  var filter = function filter(state, action) {
    if (state === undefined) {
      return initialState;
    }

    var filteredState = state;

    if (knownKeys.length) {
      filteredState = createEmpty();
      knownKeys.forEach(function (key) {
        var oldValue = getValue(state, key);
        filteredState = setValue(filteredState, key, oldValue);
      });
    }

    var newState = reducer(filteredState, action);

    if (newState === filteredState) {
      return state;
    }

    knownKeys = getKeys(newState);
    getKeys(state).forEach(function (key) {
      if (!knownKeys.includes(key)) {
        var oldValue = getValue(state, key);
        newState = setValue(newState, key, oldValue);
      }
    });
    return newState;
  };

  return filter;
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var DETACH_TYPE = '@@DYNOSTORE/DETACH_REDUCER';
var detach = function detach(identifier) {
  return {
    type: DETACH_TYPE,
    identifier: identifier
  };
};
var detachableReducer = function detachableReducer(identifier) {
  return function (reducer) {
    var detacher = function detacher(state, action) {
      return action.type !== DETACH_TYPE || action.identifier !== identifier ? reducer(state, action) : undefined;
    };

    return detacher;
  };
};
var cleanupDetachedReducer = function cleanupDetachedReducer(reducer, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$stateHandler = _ref.stateHandler;

  _ref$stateHandler = _ref$stateHandler === void 0 ? defaultStateHandler : _ref$stateHandler;
  var createEmpty = _ref$stateHandler.createEmpty,
      getKeys = _ref$stateHandler.getKeys,
      getValue = _ref$stateHandler.getValue,
      setValue = _ref$stateHandler.setValue;

  var cleaner = function cleaner(state, action) {
    var newState = reducer(state, action);

    if (action.type == DETACH_TYPE && newState !== state) {
      var hasChanged = false;
      var nextState = createEmpty();
      getKeys(newState).forEach(function (key) {
        var value = getValue(newState, key);

        if (value !== undefined) {
          nextState = setValue(nextState, key, value);
        } else {
          hasChanged = true;
        }
      });
      return hasChanged ? nextState : newState;
    } else {
      return newState;
    }
  };

  return cleaner;
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var expandReducers = function expandReducers(staticReducer, dynamicReducers, options, defaultOptions) {
  var expandedReducers = {
    reducer: staticReducer,
    children: {},
    options: defaultOptions
  };
  Object.entries(dynamicReducers).forEach(function (_ref) {
    var key = _ref[0],
        dynamicReducer = _ref[1];
    var path = key.split('.');
    var currentNode = expandedReducers;
    path.forEach(function (element) {
      if (!currentNode.children[element]) {
        currentNode.children[element] = {
          children: {},
          options: defaultOptions
        };
      }

      currentNode = currentNode.children[element];
    });
    currentNode.reducer = dynamicReducer;
    currentNode.options = options[key];
  });
  return expandedReducers;
};

var collapseReducers = function collapseReducers(node) {
  var reducer = node.reducer,
      children = node.children,
      options = node.options;
  var childrenEntries = Object.entries(children);

  if (!childrenEntries.length) {
    return reducer;
  }

  var reducersToCombine = {};
  childrenEntries.forEach(function (_ref2) {
    var key = _ref2[0],
        childNode = _ref2[1];
    reducersToCombine[key] = collapseReducers(childNode);
  });
  var childrenReducer = combineReducers(reducersToCombine, options);

  if (!reducer) {
    return childrenReducer;
  }

  var filtered = filteredReducer(reducer, options);
  var merged = mergeReducers([filtered, childrenReducer], options);
  var cleaned = cleanupDetachedReducer(merged, options);
  return cleaned;
};

var createDynamicReducer = function createDynamicReducer(staticReducer, dynamicReducers, options, defaultOptions) {
  if (options === void 0) {
    options = {};
  }

  var expandedReducers = expandReducers(staticReducer, dynamicReducers, options, defaultOptions);
  return collapseReducers(expandedReducers);
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var flattenReducers = function flattenReducers(reducers, parentKey) {
  if (typeof reducers === 'function') {
    var _ref;

    return _ref = {}, _ref[parentKey.replace(/\//g, '.')] = reducers, _ref;
  }

  return Object.keys(reducers).reduce(function (reducerMap, key) {
    return _objectSpread2(_objectSpread2({}, reducerMap), flattenReducers(reducers[key], parentKey ? parentKey + "." + key : key));
  }, {});
};

var dynamicReducersEnhancer = function dynamicReducersEnhancer(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      stateHandler = _ref.stateHandler;

  return function (createHandlers) {
    return function (store, staticReducer) {
      var defaultOptions = store.dynostoreOptions || {};
      var defaultReducerOptions = {
        stateHandler: stateHandler || defaultOptions.stateHandler || defaultStateHandler
      };
      var dynamicReducers = {};
      var dynamicReducerOptions = {};

      var createReducer = function createReducer() {
        return createDynamicReducer(staticReducer, dynamicReducers, dynamicReducerOptions, defaultReducerOptions);
      };

      var attachReducers = function attachReducers(reducers, options) {
        if (options === void 0) {
          options = {};
        }

        Object.entries(flattenReducers(reducers)).forEach(function (_ref2) {
          var identifier = _ref2[0],
              reducer = _ref2[1];
          dynamicReducers[identifier] = detachableReducer(identifier)(reducer);
          dynamicReducerOptions[identifier] = _objectSpread2(_objectSpread2({}, defaultReducerOptions), options);
        });
        store.replaceReducer(createReducer());
      };

      var detachReducers = function detachReducers(identifiers) {
        identifiers.filter(function (identifier) {
          return dynamicReducers[identifier];
        }).forEach(function (identifier) {
          delete dynamicReducers[identifier];
          delete dynamicReducerOptions[identifier];
          store.dispatch(detach(identifier));
        });
        store.replaceReducer(createReducer());
      };

      for (var _len = arguments.length, rest = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        rest[_key - 2] = arguments[_key];
      }

      var handlers = createHandlers.apply(void 0, [store, staticReducer].concat(rest));
      return _objectSpread2(_objectSpread2({}, handlers), {}, {
        attachReducers: attachReducers,
        detachReducers: detachReducers
      });
    };
  };
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var attachReducerEnhancer = function attachReducerEnhancer(reducer, options) {
  return function (identifier) {
    return function (store) {
      var _store$attachReducers;

      if (process.env.NODE_ENV !== 'production' && typeof store.attachReducers !== 'function') {
        throw new TypeError("'store.attachReducers' function is missing: Unable to attach reducer '" + identifier + "'.");
      }

      store.attachReducers((_store$attachReducers = {}, _store$attachReducers[identifier] = reducer, _store$attachReducers), options);
    };
  };
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
var dispatchActionEnhancer = function dispatchActionEnhancer(action) {
  return function () {
    return function (store) {
      store.dispatch(action);
    };
  };
};

/**
 * Copyright 2018, IOOF Holdings Limited.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

var createDynamicTarget = function createDynamicTarget(enhancers) {
  return function (identifier) {
    var enhancerChain = enhancers.map(function (enhancer) {
      return enhancer(identifier);
    });
    return function (store) {
      var storeEnhancedChain = enhancerChain.map(function (enhancer) {
        return enhancer(store);
      });
      return function (target) {
        var targetEnhancerChain = storeEnhancedChain.filter(function (enhancer) {
          return enhancer;
        });
        return compose.apply(void 0, targetEnhancerChain)(target);
      };
    };
  };
};

export default dynostore;
export { attachReducerEnhancer as attachReducer, combineReducers, createDynamicTarget, deepMerge, deepStateHandler, defaultStateHandler, dispatchActionEnhancer as dispatchAction, dynamicReducersEnhancer as dynamicReducers, filteredReducer, mergeReducers, shallowMerge, shallowStateHandler };
